import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr, fmtDate } from "../lib/api";
import { Button } from "../components/ui/button";
import { SignaturePad } from "../components/SignaturePad";
import { ListenButton } from "../components/TalkLibrary";
import { saveBlob } from "../components/CourseLibrary";

const Lesson = ({ c, i }) => {
  const l = c.lessons[i];
  return (
    <div className="panel space-y-4 p-5" data-testid={`course-lesson-${i}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="eyebrow">Lesson {i + 1} of {c.lessons.length}</p>
        <ListenButton url={`/training/courses/${c.id}/audio?lesson=${i}`} testid={`course-listen-${i}`} />
      </div>
      <h2 className="font-display text-2xl font-bold uppercase leading-tight">{l.heading}</h2>
      {l.body.map((p, k) => <p key={k} className="text-base leading-relaxed text-slate-200">{p}</p>)}
      <div className="rounded-lg border-l-4 border-rose-500 bg-rose-500/10 p-3 text-sm" data-testid={`course-key-point-${i}`}><b>Remember:</b> {l.key_point}</div>
    </div>
  );
};

const Quiz = ({ c, answers, setAnswers }) => (
  <div className="space-y-3" data-testid="course-quiz">
    {c.quiz.map((q, i) => (
      <div key={i} className="panel p-4">
        <p className="mb-2 font-semibold">{i + 1}. {q.q}</p>
        <div className="grid gap-1.5">
          {q.options.map((o, k) => (
            <button key={k} onClick={() => setAnswers(answers.map((x, j) => (j === i ? k : x)))} className={`tap rounded-lg border px-3 py-2 text-left text-sm ${answers[i] === k ? "border-rose-500 bg-rose-500/20" : "border-slate-700 hover:bg-slate-800"}`} data-testid={`course-q${i}-opt${k}`}>{o}</button>
          ))}
        </div>
      </div>
    ))}
  </div>
);

const Result = ({ c, r, retry }) => {
  const nav = useNavigate();
  return (
    <div className="panel space-y-3 p-6 text-center" data-testid="course-result">
      {r.passed ? <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-400" /> : <XCircle className="mx-auto h-14 w-14 text-red-400" />}
      <p className="font-display text-3xl font-bold uppercase" data-testid="course-result-title">{r.passed ? "Passed" : "Not passed yet"}</p>
      <p className="text-slate-300" data-testid="course-result-score">You scored {r.score}% (pass mark {r.pass_mark}%).</p>
      {r.passed ? <p className="text-sm text-slate-400">Recorded on your training record. {r.expires ? `Refresher due ${fmtDate(r.expires)}.` : ""}</p>
        : <p className="text-sm text-slate-400">Your supervisor has been told. Read lesson(s) again and retake: questions {r.wrong.map((w) => w + 1).join(", ")} were wrong.</p>}
      <div className="flex flex-wrap justify-center gap-2">
        {r.passed && <Button className="bg-rose-600 hover:bg-rose-700" onClick={() => saveBlob(`/training/completions/${r.id}/certificate`, `Certificate_${c.id}.pdf`)} data-testid="course-download-cert">Download certificate</Button>}
        {!r.passed && <Button className="bg-rose-600 hover:bg-rose-700" onClick={retry} data-testid="course-retry">Try again</Button>}
        <Button variant="outline" className="border-slate-700 bg-transparent" onClick={() => nav("/training?tab=courses")} data-testid="course-back-library">Back to courses</Button>
      </div>
    </div>
  );
};

export default function CoursePlayer() {
  const { cid } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [step, setStep] = useState(-1);
  const [answers, setAnswers] = useState([]);
  const [sig, setSig] = useState(null);
  const [ok, setOk] = useState(false);
  const [res, setRes] = useState(null);
  useEffect(() => { api.get(`/training/courses/${cid}`).then((r) => { setC(r.data); setAnswers(r.data.quiz.map(() => null)); }).catch((e) => toast.error(fmtErr(e))); }, [cid]);
  if (!c) return <p className="text-slate-500">Loading...</p>;
  const n = c.lessons.length;
  const submit = async () => {
    try { setRes((await api.post(`/training/courses/${cid}/complete`, { answers, signature: sig, understood: ok })).data); } catch (e) { toast.error(fmtErr(e)); }
  };
  const retry = () => { setRes(null); setStep(0); setAnswers(c.quiz.map(() => null)); setSig(null); setOk(false); };
  return (
    <div className="mx-auto max-w-3xl space-y-4" data-testid="course-player">
      <button onClick={() => nav("/training?tab=courses")} className="flex items-center gap-1 text-sm text-slate-400 hover:text-slate-100" data-testid="course-exit"><ArrowLeft className="h-4 w-4" />Course library</button>
      <div>
        <p className="eyebrow">{c.category} · {c.duration_mins} min</p>
        <h1 className="font-display text-3xl font-bold uppercase sm:text-4xl" data-testid="course-title">{c.title}</h1>
        <div className="mt-3 flex gap-1">{[...Array(n + 1)].map((_, i) => <span key={i} className={`h-1.5 flex-1 rounded ${i <= step ? "bg-rose-500" : "bg-slate-800"}`} />)}</div>
      </div>
      {res ? <Result c={c} r={res} retry={retry} /> : step === -1 ? (
        <div className="panel space-y-3 p-5" data-testid="course-intro">
          <p className="text-slate-200">{c.summary}</p>
          <p className="eyebrow">You will be able to</p>
          <ul className="list-disc space-y-1 pl-5 text-sm text-slate-300">{c.objectives.map((o) => <li key={o}>{o}</li>)}</ul>
          <p className="text-xs text-slate-500">Legal basis: {c.legal_basis}. Pass mark {c.pass_mark}%.</p>
          <Button className="tap bg-rose-600 hover:bg-rose-700" onClick={() => setStep(0)} data-testid="course-begin">Begin</Button>
        </div>
      ) : step < n ? <Lesson c={c} i={step} /> : (
        <>
          <Quiz c={c} answers={answers} setAnswers={setAnswers} />
          <div className="panel space-y-3 p-4">
            <label className="flex items-start gap-3 text-sm"><input type="checkbox" className="mt-1 h-5 w-5 accent-rose-600" checked={ok} onChange={(e) => setOk(e.target.checked)} data-testid="course-understood" />I confirm I have read and understood this course and will follow it at work.</label>
            <SignaturePad onChange={setSig} testid="course-signature" />
            <Button className="tap w-full bg-rose-600 hover:bg-rose-700" disabled={answers.includes(null) || !sig || !ok} onClick={submit} data-testid="course-submit">Submit answers and sign</Button>
          </div>
        </>
      )}
      {!res && step >= 0 && (
        <div className="flex justify-between">
          <Button variant="ghost" onClick={() => setStep(step - 1)} data-testid="course-prev"><ArrowLeft className="mr-1 h-4 w-4" />Back</Button>
          {step < n && <Button className="bg-slate-700 hover:bg-slate-600" onClick={() => setStep(step + 1)} data-testid="course-next">{step === n - 1 ? "Go to the check" : "Next lesson"}<ArrowRight className="ml-1 h-4 w-4" /></Button>}
        </div>
      )}
    </div>
  );
}
